import { useCallback, useMemo, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { useMqttStore } from '@stores/mqttStore';
import { useRackAssignmentsQuery } from '@api/bff/rackAssignments/useRackAssignments';
import { useCreateWorkOrderRun } from '@api/bff/runs/useRuns';
import { buildRunCells } from '@api/bff/runs/buildRunCells';
import type {
  RunStartMode,
  WorkOrderRun,
  WorkOrderRunCellRequest
} from '@api/bff/runs/useRuns';

export type StartRunBlockedReason =
  | 'disconnected'
  | 'noStation'
  | 'noAssignedCells';

export const extractApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object') {
      const record = data as Record<string, unknown>;
      const message =
        record.message ?? record.detail ?? record.title ?? record.error;
      if (typeof message === 'string' && message.trim()) return message;
    }
    if (error.message) return error.message;
  }
  if (error instanceof Error && error.message) return error.message;
  return 'Failed to start run.';
};

export const useLaunchRun = (jobNumber: string) => {
  const queryClient = useQueryClient();
  const stationId = useMqttStore(state => state.stationId);
  const { data: rackAssignment, isLoading: isAssignmentLoading } =
    useRackAssignmentsQuery(jobNumber);
  const createRun = useCreateWorkOrderRun();
  const [error, setError] = useState<string | null>(null);
  const [isLaunching, setIsLaunching] = useState(false);

  const allCells = useMemo(() => buildRunCells(rackAssignment), [rackAssignment]);

  const [startMode, setStartMode] = useState<RunStartMode>('Fresh');

  const [selectionByCell, setSelectionByCell] = useState<
    Record<string, { groups?: string[]; points?: string[] }>
  >({});

  const setCellGroups = useCallback(
    (cell: string, groups: string[] | undefined) => {
      setSelectionByCell(prev => {
        const next = { ...prev };
        const entry = { ...next[cell] };
        if (groups === undefined) delete entry.groups;
        else entry.groups = groups;
        if (entry.groups === undefined && entry.points === undefined)
          delete next[cell];
        else next[cell] = entry;
        return next;
      });
    },
    []
  );

  const setCellPoints = useCallback(
    (cell: string, points: string[] | undefined) => {
      setSelectionByCell(prev => {
        const next = { ...prev };
        const entry = { ...next[cell] };
        if (points === undefined) delete entry.points;
        else entry.points = points;
        if (entry.groups === undefined && entry.points === undefined)
          delete next[cell];
        else next[cell] = entry;
        return next;
      });
    },
    []
  );

  const getCellSelection = useCallback(
    (cell: string) => selectionByCell[cell],
    [selectionByCell]
  );

  const withSelection = useCallback(
    (cells: WorkOrderRunCellRequest[]): WorkOrderRunCellRequest[] =>
      cells.map(cell => {
        const selection = selectionByCell[cell.cell];
        if (!selection) return cell;
        return {
          ...cell,
          ...(selection.groups !== undefined && {
            selectedGroups: selection.groups
          }),
          ...(selection.points !== undefined && {
            selectedPoints: selection.points
          })
        };
      }),
    [selectionByCell]
  );

  const clearError = useCallback(() => setError(null), []);

  const launch = useCallback(
    async (cells: WorkOrderRunCellRequest[]): Promise<WorkOrderRun | null> => {
      setError(null);
      if (!stationId) {
        setError('No station device is configured.');
        return null;
      }
      if (cells.length === 0) {
        setError('No assigned cells to run for this work order.');
        return null;
      }
      setIsLaunching(true);
      try {
        const run = await createRun.mutateAsync({
          jobNumber,
          targetStationId: stationId,
          startedBy: 'system',
          cells,
          startMode
        });
        queryClient.invalidateQueries({ queryKey: ['workOrderRuns'] });
        queryClient.invalidateQueries({ queryKey: ['testRuns'] });
        return run;
      } catch (launchError) {
        setError(extractApiError(launchError));
        return null;
      } finally {
        setIsLaunching(false);
      }
    },
    [createRun, jobNumber, stationId, queryClient, startMode]
  );

  const launchFullRun = useCallback(
    () => launch(withSelection(allCells)),
    [launch, allCells, withSelection]
  );

  const launchCell = useCallback(
    (cell: string) =>
      launch(withSelection(allCells.filter(entry => entry.cell === cell))),
    [launch, allCells, withSelection]
  );

  const cellIsAssigned = useCallback(
    (cell: string) => allCells.some(entry => entry.cell === cell),
    [allCells]
  );

  const blockedReason: StartRunBlockedReason | null = !stationId
    ? 'noStation'
    : !isAssignmentLoading && allCells.length === 0
      ? 'noAssignedCells'
      : null;

  return {
    launchFullRun,
    launchCell,
    cellIsAssigned,
    allCells,
    canLaunch: !!stationId && allCells.length > 0,
    blockedReason,
    isAssignmentLoading,
    isLaunching,
    error,
    clearError,
    setCellGroups,
    setCellPoints,
    getCellSelection,
    startMode,
    setStartMode
  };
};
